// @ts-nocheck
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  CaretDown,
  Code,
  Globe,
  SquaresFour,
  List,
  PuzzlePiece,
  MagnifyingGlass,
  User,
  Users,
  X,
} from '@phosphor-icons/react';
import { Button, Text, XStack, YStack } from 'tamagui';

import { useIsMobile } from '../../hooks/useIsMobile';
import { useActiveSection } from '../../hooks/useActiveSection';
import { PRODUCT_NAME } from './constants';

const SECTION_TO_NAV: Record<string, string> = {
  search: 'Product',
  apps: 'Product',
  integrations: 'Product',
  'use-cases': 'Use Cases',
  pricing: 'Pricing',
  cta: 'Pricing',
};

interface DropdownItem {
  icon: typeof MagnifyingGlass;
  title: string;
  description: string;
  href: string;
  external?: boolean;
  route?: boolean;
}

interface NavDropdown {
  label: string;
  items: DropdownItem[];
}

const DROPDOWNS: NavDropdown[] = [
  {
    label: 'Product',
    items: [
      { icon: MagnifyingGlass, title: 'Search', description: 'Find anything across 50+ connected tools', href: '#search' },
      { icon: SquaresFour, title: 'Built-in Apps', description: 'Kanban, Calendar, Docs, Wiki, OKRs, Sprints', href: '#apps' },
      { icon: PuzzlePiece, title: 'Integrations', description: 'Gmail, Slack, Notion, GitHub, and more', href: '/integrations', route: true },
      { icon: User, title: 'Getting Started', description: 'Start using Torch in 5 minutes', href: '/getting-started', route: true },
      { icon: MagnifyingGlass, title: 'Security', description: 'Permissions-aware, encrypted, SSO', href: '/security', route: true },
    ],
  },
  {
    label: 'Use Cases',
    items: [
      { icon: User, title: 'For Individuals', description: 'Replace 5 apps with one workspace', href: '/use-cases/individuals', route: true },
      { icon: Users, title: 'For Teams', description: "Your team's knowledge, searchable", href: '/use-cases/teams', route: true },
      { icon: Code, title: 'For Engineering', description: 'Search GitHub, Jira, and Slack together', href: '/use-cases/engineering', route: true },
      { icon: Globe, title: 'For Remote Teams', description: "Your distributed team's brain", href: '/use-cases/remote', route: true },
    ],
  },
  {
    label: 'Resources',
    items: [
      { icon: Code, title: 'Torch vs Notion', description: 'Feature comparison', href: '/compare/torch-vs-notion', route: true },
      { icon: Globe, title: 'Torch vs Glean', description: 'Enterprise search comparison', href: '/compare/torch-vs-glean', route: true },
    ],
  },
];

const DIRECT_LINKS = [
  { label: 'Pricing', href: '/pricing', route: true },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileExpandedSection, setMobileExpandedSection] = useState<string | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const panelRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleNavClick = useCallback(
    (href: string, external?: boolean, route?: boolean) => {
      setMenuOpen(false);
      setActiveDropdown(null);
      setMobileExpandedSection(null);
      if (external) { window.open(href, '_blank'); return; }
      if (route) { navigate(href); return; }
      if (location.pathname !== '/') { navigate('/' + href); return; }
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    },
    [navigate, location.pathname],
  );

  const handleEnter = useCallback((label: string) => {
    if (closeTimeoutRef.current) { clearTimeout(closeTimeoutRef.current); closeTimeoutRef.current = null; }
    setActiveDropdown(label);
  }, []);

  const handleLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => setActiveDropdown(null), 200);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent, label: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setActiveDropdown(activeDropdown === label ? null : label);
    } else if (e.key === 'Escape') {
      setActiveDropdown(null);
    }
  }, [activeDropdown]);

  const activeSection = useActiveSection();
  const activeNavLabel = location.pathname === '/' && activeSection ? SECTION_TO_NAV[activeSection] || null : null;

  // Get active dropdown data + trigger position
  const activeData = DROPDOWNS.find((d) => d.label === activeDropdown);
  const activeTrigger = activeDropdown ? triggerRefs.current[activeDropdown] : null;
  const triggerRect = activeTrigger?.getBoundingClientRect();

  return (
    <>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>

      <XStack
        tag="header"
        position={'fixed' as any}
        top={0} left={0} right={0} zIndex={100}
        alignItems="center" justifyContent="space-between"
        paddingVertical="$3" paddingHorizontal="$5"
        backgroundColor={scrolled || menuOpen ? '$background' : 'transparent'}
        borderBottomWidth={scrolled || menuOpen ? 1 : 0}
        borderColor="$borderColor"
        style={{
          backdropFilter: scrolled || menuOpen ? 'blur(16px)' : 'none',
          WebkitBackdropFilter: scrolled || menuOpen ? 'blur(16px)' : 'none',
          transition: 'all 0.2s ease',
        } as any}
      >
        {/* Logo */}
        <XStack alignItems="center" gap="$3" cursor="pointer" onPress={() => navigate('/')}>
          <XStack
            width={36} height={36} borderRadius="$3"
            alignItems="center" justifyContent="center"
            backgroundColor="$accentBackground"
          >
            <img src="/torch.png" alt={PRODUCT_NAME} style={{ width: 22, height: 22 }} />
          </XStack>
          <Text fontSize={20} fontWeight="600" letterSpacing={-0.5} color="$color12">
            {PRODUCT_NAME}
          </Text>
        </XStack>

        {/* Desktop nav */}
        {!isMobile && (
          <XStack gap="$6" alignItems="center">
            {DROPDOWNS.map((dropdown) => (
              <div
                key={dropdown.label}
                ref={(el) => { triggerRefs.current[dropdown.label] = el; }}
                onMouseEnter={() => handleEnter(dropdown.label)}
                onMouseLeave={handleLeave}
                onKeyDown={(e) => handleKeyDown(e, dropdown.label)}
                tabIndex={0}
                role="button"
                aria-expanded={activeDropdown === dropdown.label}
                aria-haspopup="true"
                style={{ position: 'relative', cursor: 'pointer', userSelect: 'none', outline: 'none' }}
              >
                <XStack alignItems="center" gap={4}>
                  <Text
                    fontSize={14}
                    color={activeDropdown === dropdown.label || activeNavLabel === dropdown.label ? '$color12' : '$color9'}
                    hoverStyle={{ color: '$color12' }}
                    style={{ transition: 'color 0.15s' } as any}
                  >
                    {dropdown.label}
                  </Text>
                  <div style={{
                    display: 'inline-flex',
                    transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: activeDropdown === dropdown.label ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}>
                    <CaretDown size={14} color={activeDropdown === dropdown.label || activeNavLabel === dropdown.label ? 'var(--color12)' : 'var(--color7)'} />
                  </div>
                </XStack>
              </div>
            ))}
            {DIRECT_LINKS.map((item) => (
              <Text
                key={item.label} fontSize={14}
                color={activeNavLabel === item.label ? '$color12' : '$color9'}
                cursor="pointer"
                hoverStyle={{ color: '$color12' }}
                onPress={() => handleNavClick(item.href, (item as any).external, (item as any).route)}
                style={{ transition: 'color 0.15s' } as any}
              >
                {item.label}
              </Text>
            ))}
          </XStack>
        )}

        {/* CTA + mobile toggle */}
        <XStack gap="$3" alignItems="center">
          {!isMobile && (
            <Button
              size="$3"
              backgroundColor="$accentBackground"
              color="$accentColor" hoverStyle={{ opacity: 0.9 }}
              onPress={() => handleNavClick('#cta')}
            >
              Join Waitlist
            </Button>
          )}
          {isMobile && (
            <Button size="$3" backgroundColor="transparent" padding="$2" onPress={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X size={22} color="var(--color12)" /> : <List size={22} color="var(--color12)" />}
            </Button>
          )}
        </XStack>
      </XStack>

      {/* Shared dropdown panel — one panel that morphs between dropdown contents */}
      {!isMobile && activeData && triggerRect && (
        <div
          ref={panelRef}
          onMouseEnter={() => handleEnter(activeData.label)}
          onMouseLeave={handleLeave}
          style={{
            position: 'fixed',
            top: triggerRect.bottom + 8,
            left: Math.max(16, triggerRect.left + triggerRect.width / 2 - 180),
            zIndex: 200,
            width: 360,
            borderRadius: 8,
            border: '1px solid var(--color4)',
            backgroundColor: 'rgba(16, 16, 20, 0.97)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            boxShadow: '0 12px 48px rgba(0, 0, 0, 0.5), 0 0 0 1px var(--color2)',
            fontFamily: "'Space Grotesk', sans-serif",
            transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease',
            opacity: 1,
            padding: '8px',
          }}
        >
          <div
            key={activeData.label}
            style={{
              animation: 'fadeIn 0.2s ease',
            }}
          >
            {activeData.items.map((item) => {
              const IconComponent = item.icon;
              return (
                <div
                  key={item.title}
                  onClick={() => handleNavClick(item.href, item.external, item.route)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 12px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = 'var(--color3)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent'; }}
                >
                  <div style={{
                    width: 36, height: 36, borderRadius: 8,
                    backgroundColor: 'var(--color3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <IconComponent size={18} color="var(--accentBackground)" />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color12)' }}>
                      {item.title}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--color7)', lineHeight: '1.3' }}>
                      {item.description}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Mobile menu */}
      {isMobile && menuOpen && (
        <YStack
          position={'fixed' as any}
          top={60} left={0} right={0} bottom={0} zIndex={99}
          backgroundColor="$background"
          padding="$5" gap="$5"
          style={{ animation: 'fadeIn 0.2s ease', overflowY: 'auto' } as any}
        >
          {DROPDOWNS.map((dropdown) => {
            const isExpanded = mobileExpandedSection === dropdown.label;
            return (
              <YStack key={dropdown.label} gap="$2">
                <XStack
                  alignItems="center" justifyContent="space-between" cursor="pointer"
                  onPress={() => setMobileExpandedSection(isExpanded ? null : dropdown.label)}
                >
                  <Text fontSize={18} color="$color11" fontWeight="500">{dropdown.label}</Text>
                  <CaretDown
                    size={18} color="var(--color9)"
                    style={{
                      transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    } as any}
                  />
                </XStack>
                {isExpanded && (
                  <YStack gap="$2" paddingLeft="$3" style={{ animation: 'fadeIn 0.2s ease' } as any}>
                    {dropdown.items.map((item) => {
                      const IconComponent = item.icon;
                      return (
                        <XStack
                          key={item.title} alignItems="center" gap="$3" paddingVertical="$2"
                          cursor="pointer" onPress={() => handleNavClick(item.href, item.external, item.route)}
                        >
                          <XStack
                            width={32} height={32} borderRadius="$2" alignItems="center" justifyContent="center"
                            backgroundColor="$color3"
                          >
                            <IconComponent size={16} color="var(--accentBackground)" />
                          </XStack>
                          <YStack>
                            <Text fontSize={15} fontWeight="500" color="$color11">{item.title}</Text>
                            <Text fontSize={12} color="$color8">{item.description}</Text>
                          </YStack>
                        </XStack>
                      );
                    })}
                  </YStack>
                )}
              </YStack>
            );
          })}
          {DIRECT_LINKS.map((item) => (
            <Text
              key={item.label} fontSize={18} color="$color11" fontWeight="500" cursor="pointer"
              onPress={() => handleNavClick(item.href, (item as any).external, (item as any).route)}
            >
              {item.label}
            </Text>
          ))}
          <Button
            size="$4"
            backgroundColor="$accentBackground"
            color="$accentColor" marginTop="$4"
            onPress={() => handleNavClick('#cta')}
          >
            Join Waitlist
          </Button>
        </YStack>
      )}
    </>
  );
}
